import {
  Box,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ContributingEntityStatus,
  EspdResponseDataType,
  useListContributingEntitiesQuery,
  type EspdRequirementResponseDto,
  type EspdResponseDto,
  type PortalSourcingEventDetailDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { LabeledValue } from "../../../../../common/components/LabeledValue";
import { YesNoQuestionCard } from "../../../../../common/components/YesNoQuestionCard";
import { ContributingEntitiesSection } from "../contributing/ContributingEntitiesSection";
import { contributingRoleForTypeCode } from "../contributing/contributingEntityRoles";
import { EspdCriterionForm } from "../espd/EspdCriterionForm";
import { presentationForCriterion } from "../espd/espdPresentationText";
import { emptyEspd, espdAnswerFor } from "../espd/useEspdAnswers";

type EspdHolder = {
  espdResponse?: EspdResponseDto | null;
  derivedEspdAnswers?: EspdRequirementResponseDto[];
};
type EspdHolderPatch = { espdResponse?: EspdResponseDto };

interface Props {
  entityId: string;
  bidId?: string;
  event: PortalSourcingEventDetailDto;
  readOnly: boolean;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const SupplierInfoStep = ({
  entityId,
  bidId,
  event,
  readOnly,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();

  const supplierDeclarationCriteria = event.applicableSupplierDeclarationCriteria ?? [];
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { data: contributingEntities } = useListContributingEntitiesQuery(
    { bidId: bidId ?? "" },
    { skip: !bidId },
  );
  const rolesWithACompany = new Set(
    (contributingEntities?.items ?? [])
      .filter((entity) => entity.status !== ContributingEntityStatus.InvitationDeclined)
      .map((entity) => entity.role),
  );
  const { state, updateField, fieldStatus } = useEditor<
    EspdHolder,
    EspdHolderPatch
  >(entityId);

  const espd: EspdResponseDto = state.espdResponse ?? emptyEspd();
  const eo = espd.economicOperator ?? {
    smeIndicator: null,
    representatives: [],
  };
  const rep = eo.representatives?.[0] ?? { firstName: "", familyName: "" };

  const saveEspd = (next: EspdResponseDto) => {
    if (readOnly) return;
    updateField({ espdResponse: next }, { debounceMs: 300 });
  };
  const setSme = (yes: boolean) =>
    saveEspd({ ...espd, economicOperator: { ...eo, smeIndicator: yes } });
  const setRep = (patch: Partial<typeof rep>) =>
    saveEspd({
      ...espd,
      economicOperator: { ...eo, representatives: [{ ...rep, ...patch }] },
    });

  const isSaving =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending";

  return (
    <Box pb="8" pt="6">
      <Flex align="center" gap="2" mb="6">
        <Heading size="md">{t("About the supplier")}</Heading>
        {isSaving && <Spinner size="sm" color="smMuted" />}
      </Flex>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Identification")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="8" spacingY="4">
            <LabeledValue label={t("Supplier")} value={selectedOrg.name} />
            {selectedOrg.organizationNumber && (
              <LabeledValue
                label={t("Organization number")}
                value={selectedOrg.organizationNumber}
              />
            )}
            {selectedOrg.country?.name && (
              <LabeledValue label={t("Country")} value={selectedOrg.country.name} />
            )}
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="1">
            {t("Authorized representative")}
          </Heading>
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("The person who can sign the offer on behalf of the supplier.")}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="6" spacingY="4">
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("First name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.firstName}
                onChange={(e) => setRep({ firstName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Family name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.familyName}
                onChange={(e) => setRep({ familyName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Email")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                type="email"
                value={rep.email ?? ""}
                onChange={(e) => setRep({ email: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("Role")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.role ?? ""}
                onChange={(e) => setRep({ role: e.target.value })}
              />
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Box mb="8">
        <Heading size="sm" mb="4">
          {t("Self-declaration")}
        </Heading>
        <Stack spacing="5">
          <YesNoQuestionCard
            text={t("Is the supplier a micro, small or medium-sized enterprise (SME)?")}
            value={eo.smeIndicator ?? null}
            onChange={setSme}
            readOnly={readOnly}
          />
          {supplierDeclarationCriteria.map((criterion) => {
            const role = contributingRoleForTypeCode(criterion.typeCode);
            const ownedByPanel = bidId !== undefined && role !== undefined;
            const declaredYes = criterion.requirementGroups[0]?.requirements.some(
              (req) =>
                req.responseDataType === EspdResponseDataType.Indicator &&
                espdAnswerFor(espd, req.id)?.indicator === true,
            );
            return (
            <EspdCriterionForm
              key={criterion.id}
              criterion={criterion}
              espd={espd}
              onChange={saveEspd}
              readOnly={readOnly}
              derivedAnswers={state.derivedEspdAnswers}
              hideOwnedFields={ownedByPanel}
              awaitingNamedCompany={
                ownedByPanel && declaredYes === true && role !== undefined && !rolesWithACompany.has(role)
              }
              presentation={presentationForCriterion(criterion)}
              showStatusBorder
            >
              {bidId && (
                <ContributingEntitiesSection
                  criterion={criterion}
                  espd={espd}
                  onEspdChange={saveEspd}
                  bidId={bidId}
                  selectionCriteria={event.selectionCriteria ?? []}
                  readOnly={readOnly}
                />
              )}
            </EspdCriterionForm>
            );
          })}
        </Stack>
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};
